"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { logTrackingValidation } from "@/lib/tracking";

/**
 * Independent tracking validation component that runs regardless of Mixpanel configuration
 * This ensures validation works even when Mixpanel environment variables are missing
 */
export default function TrackingValidationScript() {
  const pathname = usePathname();

  // Run validation on mount and route changes
  useEffect(() => {
    // Only run in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    console.log('environment', process.env.NODE_ENV);
    
    // Run validation immediately
    try {
      logTrackingValidation();
    } catch (e) {
      console.error('❌ Tracking validation failed on mount:', e);
    }

    // Also run validation after a short delay to catch dynamically loaded content
    setTimeout(() => {
      try {
        logTrackingValidation();
      } catch (e) {
        console.error('❌ Tracking validation failed after delay:', e);
      }
    }, 800);

    // Observe new nodes and revalidate when new tracking IDs appear
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              if (el.hasAttribute('data-mixpanel-id') || el.querySelector('[data-mixpanel-id]')) {
                logTrackingValidation();
                return;
              }
            }
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Expose validation function globally for manual testing
    (window as any).validateMixpanelTracking = logTrackingValidation;

    return () => observer.disconnect();
  }, [pathname]);

  return null; // This component doesn't render anything
}
